import { useCallback, useMemo } from 'react';
import { useSoftphoneCallState } from '..';
import { useSoftphoneRingManager } from '../../../ring-manager/softphone-ring-manager';
import {
  EstablishedCall,
  IncomingCall,
  OutgoingCall,
  isEstablishedCall,
  isIncomingCall,
  isOutgoingCall,
} from '../../../types';
import { useSoftphoneClient } from '../../softphone-client-provider';
import { useSoftphoneEventsEmitter } from '../../softphone-events-provider';

/**
 *
 * Triggers the client hangup action, but does not update the call state.
 * Defers state updates to the 'handleHangup' handler assigned to the client delegate.
 */
export const useEndCall = () => {
  const calls = useSoftphoneCallState((ctx) => ctx.calls);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx.establishedCalls);
  const client = useSoftphoneClient((ctx) => ctx.client);
  const setIsMuted = useSoftphoneCallState((ctx) => ctx.setIsMuted);
  const { emit } = useSoftphoneEventsEmitter();
  const { handleRejectRing } = useSoftphoneRingManager();

  const endCall = useCallback(
    async <T extends EstablishedCall | OutgoingCall | IncomingCall>(call: T) => {
      setIsMuted(false);
      handleRejectRing(call.id);
      if (isOutgoingCall(call)) {
        await call.inviter.cancel({
          reasonPhrase: 'Call ended by user',
        });
      } else if (isEstablishedCall(call)) {
        await call.session.bye({});
      } else if (isIncomingCall(call)) {
        await call.invitation.reject({
          reasonPhrase: 'Call ended by user',
          statusCode: 603,
        });
      }
      return;
    },
    [client, calls]
  );

  const endAllCalls = useCallback(async () => {
    return Promise.all(establishedCalls.map((call) => endCall(call))).then(() => {
      emit('conference-call.ended', establishedCalls);
      return;
    });
  }, [endCall, establishedCalls]);

  return useMemo(
    () => ({
      endCall,
      endAllCalls,
    }),
    [endCall, endAllCalls]
  );
};
